/**
 * SupplierPayablesDesk
 * --------------------
 * Every supplier invoice and payment in one place, including the ones that
 * belong to no order at all — the historical records keyed in by hand for
 * reconciliation. Embedded per-supplier from the Suppliers screen, or stood
 * up on its own as the central desk.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Upload, FileText, Banknote, Trash2, Link2,
  CheckCircle2, XCircle, Paperclip, ShieldCheck, RefreshCw, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import SupplierPaymentDialog from "./SupplierPaymentDialog";
import PayablesRecordDialog from "./PayablesRecordDialog";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import StatementMatchPanel from "./StatementMatchPanel";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLS = {
  pending_approval: "bg-amber-100 text-amber-800",
  received: "bg-amber-50 text-amber-700",
  approved: "bg-indigo-50 text-indigo-700",
  partially_paid: "bg-orange-50 text-orange-700",
  paid: "bg-emerald-50 text-emerald-700",
  disputed: "bg-red-50 text-red-700",
  void: "bg-slate-100 text-slate-500",
  rejected: "bg-red-50 text-red-700",
};

const STATUSES = ["pending_approval", "received", "approved", "partially_paid",
  "paid", "disputed", "void", "rejected"];

// Payables attachments live behind an authenticated route — a bare <a href>
// would come back as "Invalid token", so fetch it with the header.
const Attachment = ({ att, token }) => (
  <button type="button"
    onClick={() => (att.url || "").startsWith("/api/payables")
      ? openAuthedDoc(att.url.replace(/^\/api/, ""), { token, fmt: undefined, filename: att.name })
      : window.open(att.url, "_blank", "noopener")}
    className="text-[11px] text-cyan-700 underline inline-flex items-center gap-1"
    data-testid={`payables-attachment-${att.id || att.name}`}>
    <Paperclip className="w-3 h-3" />{att.name}
  </button>
);

export const SupplierPayablesDesk = ({ token, supplierId, supplierName, embedded = false, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [recordDialog, setRecordDialog] = useState(null); // {kind, record}
  const [busyId, setBusyId] = useState("");
  const [delegates, setDelegates] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const uploadRef = useRef({ kind: "", id: "", docType: "" });
  const fileInput = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payables/desk`, {
        params: {
          q: q.trim() || undefined,
          supplier_id: supplierId || undefined,
          status: status || undefined,
          unlinked_only: unlinkedOnly || undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the payables desk");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, supplierId, status, unlinkedOnly, q]);

  useEffect(() => { load(); }, [load]);

  // After a mutation: refresh the list and let the parent ledger re-read its
  // own roll-up. Never called from load() itself — that would loop.
  const reload = useCallback(async () => { await load(); onChanged?.(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load]);

  const loadAccess = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payables/access`, { headers });
      setDelegates(r.data?.delegates || []);
      setIsOwner(!!r.data?.is_owner);
    } catch { /* read-only users simply do not see the panel */ }
  }, [headers]);

  useEffect(() => { if (!embedded) loadAccess(); }, [embedded, loadAccess]);

  const invoices = data?.invoices || [];
  const payments = data?.payments || [];
  const totals = data?.totals || {};
  const canWrite = data?.can_write;
  const pending = invoices.filter(i => i.status === "pending_approval");
  const dupeInvoices = data?.duplicate_invoices || {};
  const dupePayments = data?.duplicate_payments || {};
  const overpaid = data?.overpaid || [];

  // ------------------------------------------------------------- mutations
  const decide = async (inv, approve) => {
    const reason = approve ? "" : (window.prompt(
      `Reject invoice ${inv.invoice_number} from ${inv.supplier_name}. Reason (they will see it):`) || "");
    if (!approve && !reason.trim()) return;
    setBusyId(inv.id);
    try {
      await axios.post(`${API}/payables/invoices/${inv.id}/${approve ? "approve" : "reject"}`,
        approve ? {} : { reason }, { headers });
      toast.success(approve ? "Invoice approved into the ledger" : "Invoice rejected");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update that invoice");
    } finally { setBusyId(""); }
  };

  const remove = async (kind, rec, label) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/payables/${kind}/${rec.id}`, { headers });
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete that record"); }
  };

  const relink = async (kind, rec) => {
    const id = window.prompt(
      "Back-link to which order? Paste the order / work order ID (blank = leave unlinked):",
      rec.link_id === "standalone" ? "" : rec.link_id);
    if (id === null) return;
    const trimmed = id.trim();
    try {
      await axios.post(`${API}/payables/${kind}/${rec.id}/link`,
        trimmed
          ? { link_type: trimmed.startsWith("WO") || trimmed.startsWith("MR") ? "work_order" : "backorder", link_id: trimmed }
          : { link_type: "standalone", link_id: "standalone" },
        { headers });
      toast.success(trimmed ? `Linked to ${trimmed}` : "Left unlinked");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link that record"); }
  };

  const pickFile = (kind, id, docType) => {
    uploadRef.current = { kind, id, docType };
    fileInput.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const { kind, id, docType } = uploadRef.current;
    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docType);
    setBusyId(id);
    try {
      await axios.post(`${API}/payables/${kind}/${id}/attachments`, form, { headers });
      toast.success(`${file.name} attached`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusyId(""); }
  };

  const grant = async () => {
    const email = window.prompt("Email address of the user who may enter payables:");
    if (!email?.trim()) return;
    try {
      await axios.post(`${API}/payables/access`, { email: email.trim() }, { headers });
      toast.success(`${email.trim()} can now enter payables`);
      loadAccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant access"); }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke payables access for ${email}?`)) return;
    try {
      await axios.delete(`${API}/payables/access/${encodeURIComponent(email)}`, { headers });
      toast.success("Access revoked");
      loadAccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke access"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading payables…
      </div>
    );
  }

  const Stat = ({ label, value, tone = "slate", testid }) => (
    <div className={`rounded-lg border border-${tone}-200 bg-${tone}-50 px-3 py-2`} data-testid={testid}>
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-lg font-black text-slate-900">{value}</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="supplier-payables-desk">
      <input type="file" ref={fileInput} onChange={onFile} className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.doc,.docx" data-testid="payables-file-input" />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Invoiced" value={money(totals.invoiced)} testid="payables-total-invoiced" />
        <Stat label="Paid" value={money(totals.paid_against_invoices)} testid="payables-total-paid" />
        <Stat label="Open balance" value={money(totals.open_balance)} testid="payables-total-open" />
        <Stat label="Unlinked records" value={totals.unlinked ?? 0} testid="payables-total-unlinked" />
        <Stat label="Awaiting approval" value={totals.pending_approval ?? 0} testid="payables-total-pending" />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Supplier, invoice #, PO #, reference…"
            className="pl-9 h-9 text-sm" data-testid="payables-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md border border-slate-300 text-sm px-2 bg-white"
          data-testid="payables-status-filter">
          <option value="">All invoice statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>Invoices: {s.replace(/_/g, " ")}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <input type="checkbox" checked={unlinkedOnly}
            onChange={(e) => setUnlinkedOnly(e.target.checked)}
            data-testid="payables-unlinked-filter" />
          No order linked
        </label>
        <Button size="sm" variant="outline" onClick={load} className="h-9 gap-1.5"
          data-testid="payables-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
        {canWrite && (
          <>
            <Button size="sm" onClick={() => setRecordDialog({ kind: "invoice", record: null })}
              className="h-9 bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="payables-new-invoice">
              <Plus className="w-3.5 h-3.5" /> Enter invoice
            </Button>
            <Button size="sm" onClick={() => setRecordDialog({ kind: "payment", record: null })}
              className="h-9 bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="payables-new-payment">
              <Banknote className="w-3.5 h-3.5" /> Record payment
            </Button>
            {invoices.some(i => (i.balance ?? 0) > 0.009) && (
              <Button size="sm" variant="outline" onClick={() => setPayOpen(true)}
                className="h-9 border-emerald-300 text-emerald-700 gap-1.5"
                data-testid="payables-pay-invoices">
                <Banknote className="w-3.5 h-3.5" /> Pay open invoices
              </Button>
            )}
          </>
        )}
      </div>

      {!canWrite && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          You can read this ledger but not change it — the super admin grants entry rights.
        </div>
      )}

      {/* Pending portal submissions */}
      {pending.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3" data-testid="payables-pending-block">
          <p className="text-[11px] font-black uppercase tracking-wider text-amber-900 mb-2">
            Submitted from the portal — awaiting your approval ({pending.length})
          </p>
          <div className="space-y-2">
            {pending.map(inv => (
              <div key={inv.id} className="bg-white rounded-md border border-amber-200 px-3 py-2 flex flex-wrap items-center gap-2"
                data-testid={`payables-pending-${inv.id}`}>
                <span className="font-bold text-sm text-slate-900">{inv.supplier_name}</span>
                <span className="text-xs text-slate-500">#{inv.invoice_number}</span>
                <span className="text-sm font-black text-slate-900">{money(inv.amount)}</span>
                {(inv.attachments || []).map(a => (
                  <Attachment key={a.id || a.url} att={a} token={token} />
                ))}
                <span className="flex-1" />
                {canWrite && (
                  <>
                    <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 gap-1 text-[11px]"
                      disabled={busyId === inv.id} onClick={() => decide(inv, true)}
                      data-testid={`payables-approve-${inv.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700 gap-1 text-[11px]"
                      disabled={busyId === inv.id} onClick={() => decide(inv, false)}
                      data-testid={`payables-reject-${inv.id}`}>
                      <XCircle className="w-3 h-3" /> Reject
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duplicate / double-payment watch */}
      {(Object.keys(dupeInvoices).length > 0 || Object.keys(dupePayments).length > 0 || overpaid.length > 0) && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50/70 px-3 py-2.5 text-xs text-red-900"
          data-testid="payables-duplicate-watch">
          <p className="font-black uppercase tracking-wider text-[11px] mb-1 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Possible double payments
          </p>
          <ul className="space-y-0.5">
            {Object.keys(dupeInvoices).length > 0 && (
              <li>{Object.keys(dupeInvoices).length} invoices share a number with another invoice from the same supplier.</li>
            )}
            {Object.keys(dupePayments).length > 0 && (
              <li>{Object.keys(dupePayments).length} payments match another on vendor, amount and reference or date.</li>
            )}
            {overpaid.map(o => (
              <li key={o.id}>
                <strong>{o.supplier_name} #{o.invoice_number}</strong> is paid {money(o.paid_total)} against a {money(o.amount)} invoice.
              </li>
            ))}
          </ul>
          <p className="text-[11px] mt-1 text-red-800/80">Flagged rows are marked below — nothing is blocked, but check before paying again.</p>
        </div>
      )}

      <StatementMatchPanel
        token={token}
        supplierId={supplierId}
        supplierName={supplierName}
        canWrite={canWrite}
        onChanged={reload}
      />

      {/* Invoices */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 text-white px-3 py-2 text-[11px] font-black uppercase tracking-wider flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Supplier invoices ({invoices.length})
        </div>
        {invoices.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-slate-400">
            No invoices yet{supplierName ? ` for ${supplierName}` : ""} — use <strong>Enter invoice</strong> to key one in by hand.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {invoices.map(inv => (
              <div key={inv.id} className="px-3 py-2.5 hover:bg-slate-50" data-testid={`payables-invoice-${inv.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-bold text-sm text-slate-900 min-w-[140px]">{inv.supplier_name}</span>
                  <span className="text-xs font-mono text-slate-600">#{inv.invoice_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${STATUS_CLS[inv.status] || "bg-slate-100 text-slate-600"}`}>
                    {(inv.status || "").replace(/_/g, " ")}
                  </span>
                  {(inv.link_type || "standalone") === "standalone"
                    ? <span className="text-[10px] font-bold text-slate-400 uppercase">no order linked</span>
                    : <span className="text-[10px] font-bold text-cyan-700 uppercase">{inv.link_id}</span>}
                  {dupeInvoices[inv.id] && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-red-100 text-red-800"
                      title="Another invoice with this number is on file for this supplier"
                      data-testid={`payables-dupe-invoice-${inv.id}`}>
                      possible duplicate
                    </span>
                  )}
                  {(Number(inv.paid_total) || 0) - (Number(inv.amount) || 0) > 0.009 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-red-600 text-white"
                      title="Paid more than the invoice face value"
                      data-testid={`payables-overpaid-${inv.id}`}>
                      paid twice?
                    </span>
                  )}
                  <span className="flex-1" />
                  <span className="text-xs text-slate-500">{inv.invoice_date || "—"}</span>
                  <span className="text-sm font-black text-slate-900 w-24 text-right">{money(inv.amount)}</span>
                  <span className="text-xs text-emerald-700 w-24 text-right">{money(inv.paid_total)} paid</span>
                  <span className={`text-xs font-bold w-24 text-right ${(inv.balance || 0) > 0.009 ? "text-orange-700" : "text-slate-400"}`}>
                    {money(inv.balance)} open
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {(inv.attachments || []).map(a => (
                    <Attachment key={a.id || a.url} att={a} token={token} />
                  ))}
                  {canWrite && (
                    <>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        disabled={busyId === inv.id}
                        onClick={() => pickFile("invoices", inv.id, "invoice")}
                        data-testid={`payables-invoice-upload-${inv.id}`}>
                        <Upload className="w-3 h-3 mr-1" /> Invoice PDF
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        onClick={() => setRecordDialog({ kind: "invoice", record: inv })}
                        data-testid={`payables-invoice-edit-${inv.id}`}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        onClick={() => relink("invoices", inv)}
                        data-testid={`payables-invoice-link-${inv.id}`}>
                        <Link2 className="w-3 h-3 mr-1" /> Link order
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-red-600"
                        onClick={() => remove("invoices", inv, `invoice #${inv.invoice_number}`)}
                        data-testid={`payables-invoice-delete-${inv.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payments */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="bg-emerald-700 text-white px-3 py-2 text-[11px] font-black uppercase tracking-wider flex items-center gap-2">
          <Banknote className="w-3.5 h-3.5" /> Payments made ({payments.length})
        </div>
        {payments.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-slate-400">
            Nothing recorded yet — <strong>Record payment</strong> keys in a historical ACH, wire or check.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {payments.map(p => (
              <div key={p.id} className="px-3 py-2.5 hover:bg-slate-50" data-testid={`payables-payment-${p.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-bold text-sm text-slate-900 min-w-[140px]">{p.supplier_name || "—"}</span>
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                    {(p.method || "").replace(/_/g, " ")}
                  </span>
                  {p.invoice_number
                    ? <span className="text-xs font-mono text-slate-600">inv #{p.invoice_number}</span>
                    : <span className="text-[10px] font-bold text-amber-700 uppercase">no invoice on file</span>}
                  {p.reference && <span className="text-xs text-slate-500">ref {p.reference}</span>}
                  {(p.link_type || "standalone") === "standalone"
                    ? <span className="text-[10px] font-bold text-slate-400 uppercase">no order linked</span>
                    : <span className="text-[10px] font-bold text-cyan-700 uppercase">{p.link_id}</span>}
                  {dupePayments[p.id] && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-red-100 text-red-800"
                      title="Same supplier, same amount, same reference or date as another payment"
                      data-testid={`payables-dupe-payment-${p.id}`}>
                      possible duplicate
                    </span>
                  )}
                  <span className="flex-1" />
                  <span className="text-xs text-slate-500">{p.payment_date || "—"}</span>
                  <span className="text-sm font-black text-emerald-800 w-24 text-right">{money(p.amount)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {(p.attachments || []).map(a => (
                    <Attachment key={a.id || a.url} att={a} token={token} />
                  ))}
                  {canWrite && (
                    <>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        disabled={busyId === p.id}
                        onClick={() => pickFile("payments", p.id, "payment_confirmation")}
                        data-testid={`payables-payment-upload-${p.id}`}>
                        <Upload className="w-3 h-3 mr-1" /> Bank / check image
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        onClick={() => setRecordDialog({ kind: "payment", record: p })}
                        data-testid={`payables-payment-edit-${p.id}`}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-slate-600"
                        onClick={() => relink("payments", p)}
                        data-testid={`payables-payment-link-${p.id}`}>
                        <Link2 className="w-3 h-3 mr-1" /> Link order
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] font-bold text-red-600"
                        onClick={() => remove("payments", p, `payment ${money(p.amount)}`)}
                        data-testid={`payables-payment-delete-${p.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Who may enter payables */}
      {!embedded && isOwner && (
        <div className="rounded-lg border border-slate-200 p-3" data-testid="payables-access-block">
          <button onClick={() => setShowAccess(v => !v)}
            className="text-[11px] font-black uppercase tracking-wider text-slate-600 inline-flex items-center gap-1.5"
            data-testid="payables-access-toggle">
            <ShieldCheck className="w-3.5 h-3.5" /> Who may enter payables ({delegates.length + 1})
          </button>
          {showAccess && (
            <div className="mt-2 space-y-1.5">
              <p className="text-xs text-slate-500">
                You always have access. Anyone added here can enter and edit invoices and payments.
              </p>
              {delegates.map(d => (
                <div key={d.email} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5">
                  <span className="font-semibold text-slate-800">{d.name || d.email}</span>
                  <span className="text-slate-500">{d.email}</span>
                  <span className="flex-1" />
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-red-600"
                    onClick={() => revoke(d.email)} data-testid={`payables-revoke-${d.email}`}>
                    Revoke
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={grant} className="h-7 text-[11px] gap-1.5"
                data-testid="payables-grant-access">
                <Plus className="w-3 h-3" /> Grant access to a user
              </Button>
            </div>
          )}
        </div>
      )}

      {payOpen && (
        <SupplierPaymentDialog
          open={payOpen}
          onClose={() => setPayOpen(false)}
          token={token}
          linkType="standalone"
          linkId="standalone"
          invoices={invoices}
          onSaved={reload}
        />
      )}

      {recordDialog && (
        <PayablesRecordDialog
          kind={recordDialog.kind}
          record={recordDialog.record}
          token={token}
          defaultSupplier={supplierId ? { id: supplierId, name: supplierName } : null}
          invoices={invoices}
          onClose={() => setRecordDialog(null)}
          onSaved={() => { setRecordDialog(null); reload(); }}
        />
      )}
    </div>
  );
};

export default SupplierPayablesDesk;
